import { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, Users, Clock, Check, AlertCircle, RefreshCw, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SESSION_MINUTES = 15;

/* ── Format a UTC ISO timestamp into display parts ─────────────── */
function formatRedemptionStamp(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  const DAYS   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return {
    day:     DAYS[d.getDay()],
    month:   MONTHS[d.getMonth()],
    date:    d.getDate(),
    year:    d.getFullYear(),
    dateStr: `${MONTHS[d.getMonth()]} ${d.getDate()}`,
    time:    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
  };
}

/* ── Live ticking clock ─────────────────────────────────────────── */
function LiveClock() {
  const [t, setT] = useState('');
  useEffect(() => {
    const tick = () => setT(new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="font-mono text-[10px] tabular-nums">{t}</span>
  );
}

/* ─── Countdown timer ─────────────────────────────────────────── */
function Countdown({ expiresAt, onExpire }) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const tick = () => {
      const left = Math.max(0, Math.ceil((new Date(expiresAt) - new Date()) / 1000));
      setSecs(left);
      if (left === 0) onExpire?.();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, onExpire]);

  const mins = String(Math.floor(secs / 60)).padStart(2, '0');
  const s    = String(secs % 60).padStart(2, '0');
  const pct  = Math.min(100, (secs / (SESSION_MINUTES * 60)) * 100);
  const urgent = secs < 60;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className={`text-xs font-semibold flex items-center gap-1.5 ${urgent ? 'text-red-500' : 'text-[#8A9A8B]'}`}>
          <Clock size={11} /> Claim expires in
        </p>
        <span className={`text-lg font-bold font-mono tabular-nums ${urgent ? 'text-red-500' : 'text-[#2B2B2B]'}`}>
          {mins}:{s}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-[#8A9A8B]/15 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ${urgent ? 'bg-red-400' : 'bg-[#C8A97E]'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* ─── 4-digit PIN input (letter is fixed per deal, auto-appended by server) ── */
function PinInput({ value, onChange, disabled, hasError }) {
  // value is 4-digit string only
  const digits     = (value + '    ').slice(0, 4).split('');
  const digitRefs  = [useRef(), useRef(), useRef(), useRef()];

  const updateValue = (idx, char) => {
    if (char && !/^\d$/.test(char)) return;
    const arr = digits.map((d, i) => (i === idx ? (char || ' ') : d));
    const newVal = arr.join('').trimEnd().replace(/ /g, '');
    onChange(newVal);
    if (char && idx < 3) setTimeout(() => digitRefs[idx + 1].current?.focus(), 0);
  };

  const handleKey = (idx, e) => {
    if (e.key === 'Backspace') {
      if (!digits[idx] || digits[idx] === ' ') {
        if (idx > 0) {
          const arr = digits.map((d, i) => (i === idx - 1 ? ' ' : d));
          onChange(arr.join('').trimEnd().replace(/ /g, ''));
          setTimeout(() => digitRefs[idx - 1].current?.focus(), 0);
        }
      } else {
        updateValue(idx, '');
      }
      e.preventDefault();
    } else if (e.key >= '0' && e.key <= '9') {
      updateValue(idx, e.key);
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    const raw = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (raw.length) {
      onChange(raw);
      setTimeout(() => digitRefs[Math.min(raw.length, 3)].current?.focus(), 0);
    }
    e.preventDefault();
  };

  const baseStyle   = `h-14 text-center text-2xl font-bold rounded-2xl border-2 transition-all outline-none focus:scale-105 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'}`;
  const filledStyle = 'bg-[#2B2B2B] text-[#F6F1E9] border-[#2B2B2B]';
  const emptyStyle  = hasError
    ? 'bg-red-50 border-red-300 text-red-500'
    : 'bg-[#F6F1E9] border-[#8A9A8B]/30 text-[#2B2B2B] focus:border-[#C8A97E] focus:bg-white';

  return (
    <div className="flex gap-2 justify-center items-center" data-testid="pin-input-group">
      {[0, 1, 2, 3].map(i => (
        <input
          key={i}
          ref={digitRefs[i]}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          value={digits[i]?.trim() || ''}
          onChange={() => {}}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={e => e.target.select()}
          disabled={disabled}
          className={`w-12 ${baseStyle} ${digits[i]?.trim() ? filledStyle : emptyStyle}`}
          data-testid={`pin-digit-${i}`}
        />
      ))}
    </div>
  );
}

/* ─── Main component ───────────────────────────────────────────── */
export default function ClaimCodeDisplay({ deal, onClaimed }) {
  const [phase, setPhase] = useState('checking'); // checking|idle|loading|active|verifying|redeemed|expired|daily_limit|wrong_pin
  const [claimData, setClaimData] = useState(null);
  const [pin, setPin] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [redemptionTime, setRedemptionTime] = useState(null);

  /* Check if user already has an active claim today */
  useEffect(() => {
    fetch(`${API}/deals/${deal.deal_id}/my-claim`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.has_claim) {
          setClaimData(d);
          const now = new Date().toISOString();
          if (d.is_redeemed) { setRedemptionTime(d.redeemed_at || new Date().toISOString()); setPhase('redeemed'); }
          else if (d.is_expired || (d.expires_at || '') < now) setPhase('idle');
          else setPhase('active');
        } else setPhase('idle');
      })
      .catch(() => setPhase('idle'));
  }, [deal.deal_id]);

  const handleClaim = async () => {
    setPhase('loading');
    try {
      const res = await fetch(`${API}/deals/${deal.deal_id}/claim`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401) { setPhase('idle'); return; }
        throw new Error(data.detail || 'Failed');
      }
      if (data.already_claimed) {
        setClaimData(data);
        const now = new Date().toISOString();
        if (data.is_redeemed) { setRedemptionTime(data.redeemed_at || now); setPhase('redeemed'); }
        else if (data.is_expired || (data.expires_at || '') < now) setPhase('idle');
        else setPhase('active');
        return;
      }
      setClaimData(data);
      setPin('');
      setErrorMsg('');
      setFailedAttempts(0);
      setPhase('active');
      onClaimed?.();
    } catch (err) { setPhase('idle'); alert(err.message); }
  };

  const handleVerify = async () => {
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      setErrorMsg('Enter the 4-digit PIN from the cashier.');
      return;
    }
    setPhase('verifying');
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/deals/${deal.deal_id}/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin, claim_id: claimData.claim_id }),
      });
      const data = await res.json();
      if (data.success) {
        setRedemptionTime(data.redeemed_at || new Date().toISOString());
        setPhase('redeemed');
      } else if (data.reason === 'expired') {
        setPhase('expired');
      } else if (data.reason === 'daily_limit') {
        setPhase('daily_limit');
      } else if (data.reason === 'wrong_pin') {
        const attempts = data.attempts || (failedAttempts + 1);
        setFailedAttempts(attempts);
        setErrorMsg(data.message || 'Incorrect PIN. Try again.');
        setPin('');
        setPhase('active');
      } else {
        setErrorMsg(data.message || 'Something went wrong.');
        setPhase('active');
      }
    } catch { setErrorMsg('Network error. Try again.'); setPhase('active'); }
  };

  const handleExpire = useCallback(() => setPhase('expired'), []);

  /* ── CHECKING ── */
  if (phase === 'checking') return (
    <div className="flex items-center justify-center py-5">
      <div className="w-5 h-5 border-2 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  /* ── IDLE ── */
  if (phase === 'idle') return (
    <div data-testid="claim-deal-section">
      {deal.age_restriction === '21+' && (
        <div className="flex items-center gap-2 mb-3 rounded-xl bg-[#8B1A1A]/8 border border-[#8B1A1A]/20 px-3 py-2" data-testid="age-warning-claim">
          <span className="text-xs font-bold text-[#8B1A1A] flex-shrink-0">21+</span>
          <p className="text-xs text-[#8B1A1A]/80">Staff will verify your ID at redemption. Must be 21 or older.</p>
        </div>
      )}
      <p className="text-xs text-[#2B2B2B]/50 flex items-center gap-1.5 mb-3">
        <Users size={12} />
        {(deal.claim_count || 0) > 0 ? `${deal.claim_count} students claimed today` : 'Be the first to claim!'}
      </p>
      <button onClick={handleClaim} data-testid="claim-deal-btn"
        className="w-full btn-primary justify-center py-4 text-base">
        <Sparkles size={16} /> Claim This Deal
      </button>
      <p className="text-xs text-center text-[#2B2B2B]/40 mt-2">
        You&apos;ll get a 15-min window to enter the business PIN
      </p>
    </div>
  );

  /* ── LOADING ── */
  if (phase === 'loading') return (
    <div className="flex flex-col items-center py-6 gap-3">
      <div className="w-8 h-8 border-3 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-[#2B2B2B]/55">Starting your claim session...</p>
    </div>
  );

  /* ── ACTIVE — show PIN entry ── */
  if ((phase === 'active' || phase === 'verifying') && claimData) return (
    <div className="rounded-2xl overflow-hidden border-2 border-[#C8A97E]/55 shadow-md" data-testid="pin-entry-section">
      {/* Header */}
      <div className="bg-[#2B2B2B] px-5 py-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#C8A97E]/20 flex items-center justify-center flex-shrink-0">
            <Sparkles size={15} className="text-[#C8A97E]" />
          </div>
          <div>
            <p className="text-white font-bold text-sm">Claim Active — Enter PIN</p>
            <p className="text-white/55 text-xs">Ask {deal.business_name} for today&apos;s 4-digit PIN</p>
          </div>
        </div>
        <Countdown expiresAt={claimData.expires_at} onExpire={handleExpire} />
      </div>

      {/* Deal letter badge + PIN input */}
      <div className="bg-[#F6F1E9] px-5 py-5">
        {/* Fixed deal letter indicator */}
        {deal.deal_letter && (
          <div className="flex items-center justify-center gap-2 mb-4" data-testid="deal-letter-badge">
            <span className="text-xs text-[#2B2B2B]/50 uppercase tracking-wider font-semibold">This deal&apos;s code:</span>
            <span className="w-8 h-8 rounded-full bg-[#C8A97E] text-white text-base font-black flex items-center justify-center shadow-sm">
              {deal.deal_letter}
            </span>
            <span className="text-xs text-[#2B2B2B]/50">(fixed for this deal)</span>
          </div>
        )}

        <p className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] text-center mb-4">
          Enter today&apos;s 4-digit PIN from {deal.business_name}
        </p>
        <PinInput
          value={pin}
          onChange={setPin}
          disabled={phase === 'verifying'}
          hasError={!!errorMsg}
        />

        {/* Show what the full PIN looks like */}
        {pin.length === 4 && deal.deal_letter && (
          <p className="text-xs text-center text-[#2B2B2B]/40 mt-2 font-mono">
            Full code: <strong className="text-[#2B2B2B]/70">{pin}{deal.deal_letter}</strong>
          </p>
        )}

        {!pin.length && (
          <p className="text-xs text-center text-[#2B2B2B]/45 mt-3">
            Staff shares only the 4 numbers — the letter is automatic
          </p>
        )}

        {errorMsg && (
          <div className="flex items-center gap-2 mt-3 text-red-500 text-xs font-semibold justify-center" data-testid="pin-error">
            <AlertCircle size={13} /> {errorMsg}
            {failedAttempts > 0 && <span className="text-red-400">({failedAttempts} attempt{failedAttempts > 1 ? 's' : ''})</span>}
          </div>
        )}

        <button
          onClick={handleVerify}
          disabled={pin.length < 4 || phase === 'verifying'}
          data-testid="verify-redeem-btn"
          className="w-full mt-4 btn-primary justify-center py-3.5 text-sm disabled:opacity-50"
        >
          {phase === 'verifying'
            ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Verifying...</>
            : <><Check size={15} /> Verify & Redeem</>}
        </button>

        <p className="text-xs text-center text-[#2B2B2B]/40 mt-3 leading-relaxed">
          The business tells you the 4-digit PIN — the letter is matched automatically.
        </p>
      </div>

      {/* Claim info footer */}
      <div className="px-5 py-3 bg-white border-t border-[#8A9A8B]/12">
        <p className="text-xs text-[#2B2B2B]/50 flex items-center gap-2">
          <Users size={11} /> {(deal.claim_count || 0)} students claimed · 1 per customer per day
        </p>
      </div>
    </div>
  );

  /* ── REDEEMED ── */
  if (phase === 'redeemed') {
    const stamp = formatRedemptionStamp(redemptionTime || new Date().toISOString());
    return (
      <div className="rounded-2xl overflow-hidden shadow-lg border border-[#C8A97E]/30" data-testid="claim-redeemed">

        {/* ── Dark header ── */}
        <div className="bg-[#1E1A14] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full border-2 border-[#C8A97E] flex items-center justify-center flex-shrink-0">
              <Check size={15} className="text-[#C8A97E]" strokeWidth={3} />
            </div>
            <div>
              <p className="text-[#C8A97E] font-black text-xs uppercase tracking-[0.22em]">Redeemed</p>
              <p className="text-white/55 text-xs mt-0.5 truncate max-w-[160px]">{deal.title}</p>
            </div>
          </div>
          {/* Live anti-screenshot clock */}
          <div className="flex flex-col items-end gap-0.5">
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
              <span className="text-[9px] text-white/35 uppercase tracking-wider font-semibold">Live</span>
            </div>
            <span className="text-white/40 font-mono text-[10px] tabular-nums"><LiveClock /></span>
          </div>
        </div>

        {/* ── Redemption stamp body ── */}
        <div className="bg-[#FAF7F0] px-5 pt-5 pb-4">
          <p className="text-[9px] font-black uppercase tracking-[0.28em] text-[#8A9A8B] text-center mb-4">
            Redemption Stamp
          </p>

          {/* Date block */}
          <div className="text-center mb-4">
            <p className="text-2xl font-bold text-[#2B2B2B] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              {stamp.day}
            </p>
            <p className="text-3xl font-black text-[#C8A97E] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              {stamp.dateStr}
            </p>
            <p className="text-lg font-semibold text-[#2B2B2B]/60 mt-1 font-mono tracking-wider">
              {stamp.time}
            </p>
          </div>

          {/* Decorative divider */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 h-px bg-[#C8A97E]/25" />
            <span className="text-[#C8A97E]/60 text-xs">✦</span>
            <div className="flex-1 h-px bg-[#C8A97E]/25" />
          </div>

          {/* Staff verification callout */}
          <div className="flex gap-2.5 bg-[#2B2B2B]/5 rounded-xl px-3.5 py-3">
            <Shield size={13} className="text-[#8A9A8B] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[9px] font-black uppercase tracking-wider text-[#8A9A8B] mb-0.5">Staff: Verify</p>
              <p className="text-xs text-[#2B2B2B]/60 leading-snug">
                Confirm stamp shows <strong className="text-[#2B2B2B]/80">today's date</strong>. Prior-day screenshots are invalid.
              </p>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-5 py-3 bg-white border-t border-[#8A9A8B]/12 flex items-center justify-between">
          <p className="text-xs text-[#2B2B2B]/45">Come back tomorrow · new PIN</p>
          <p className="text-xs text-[#8A9A8B] flex items-center gap-1">
            <Users size={10} /> {(deal.claim_count || 0)}
          </p>
        </div>
      </div>
    );
  }

  /* ── EXPIRED ── */
  if (phase === 'expired') return (
    <div className="rounded-2xl bg-red-50 border border-red-200 p-5" data-testid="claim-expired">
      <div className="flex items-center gap-3 mb-3">
        <Clock size={18} className="text-red-400 flex-shrink-0" />
        <div>
          <p className="font-bold text-red-700 text-sm">Session Expired</p>
          <p className="text-xs text-red-600/75">Your 15-minute window passed. Tap below to start a new session.</p>
        </div>
      </div>
      <button onClick={handleClaim} className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors">
        <RefreshCw size={14} /> Try Again
      </button>
    </div>
  );

  /* ── DAILY LIMIT ── */
  if (phase === 'daily_limit') return (
    <div className="rounded-2xl bg-[#F6F1E9] border border-[#8A9A8B]/25 p-5" data-testid="claim-daily-limit">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#C8A97E]/15 flex items-center justify-center flex-shrink-0">
          <Check size={16} className="text-[#C8A97E]" />
        </div>
        <div>
          <p className="font-bold text-[#2B2B2B] text-sm">Already Claimed Today</p>
          <p className="text-xs text-[#2B2B2B]/55">Come back tomorrow for a fresh PIN and new session.</p>
        </div>
      </div>
    </div>
  );

  return null;
}
